import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Typography,
  Divider,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Alert,
} from "@mui/material";
import { apiClient } from "../services/apiClient";

export interface InventoryDetailRow {
  locationId18: number;
  locationName: string;
  quantityOnHand: number;
  reservedQty: number;
  availableQty: number;
  lastSyncAt: string | null;
  productId18: number | null;
}

interface Props {
  open: boolean;
  unifiedProductId: string | null;
  productName?: string | undefined;
  onClose: () => void;
}

export function InventoryDetailsDialog({
  open,
  unifiedProductId,
  productName,
  onClose,
}: Props): React.ReactElement {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<InventoryDetailRow[]>([]);

  useEffect(() => {
    let active = true;
    async function load() {
      if (!open || !unifiedProductId) return;
      setLoading(true);
      setError(null);
      try {
        const res = await apiClient.get<{
          status: string;
          data: InventoryDetailRow[];
        }>(`/forecasting/product-inventory/${encodeURIComponent(unifiedProductId)}`);
        const json = res.data;
        if (json?.status === "success" && Array.isArray(json.data)) {
          if (active) setRows(json.data);
        } else {
          throw new Error("Respuesta inválida del servidor");
        }
      } catch (e) {
        if (active) setError("No se pudo cargar el inventario");
        console.error("Inventory details fetch error:", e);
      } finally {
        if (active) setLoading(false);
      }
    }
    load();
    return () => {
      active = false;
    };
  }, [open, unifiedProductId]);

  const totals = useMemo(() => {
    const totalOnHand = rows.reduce(
      (sum, r) => sum + (r.quantityOnHand ?? 0),
      0,
    );
    return { totalOnHand };
  }, [rows]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="h6" component="div" fontWeight={600}>
            Inventario por ubicación
          </Typography>
        </Stack>
        {productName && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {productName}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{ minHeight: 200 }}
          >
            <CircularProgress size={32} />
            <Typography variant="body2" sx={{ mt: 1 }}>
              Cargando inventario...
            </Typography>
          </Stack>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : rows.length === 0 ? (
          <Alert severity="info">Sin inventario disponible.</Alert>
        ) : (
          <Stack spacing={2}>
            <Stack direction="row" spacing={2}>
              <Typography variant="body2">
                En mano: <b>{totals.totalOnHand}</b>
              </Typography>
            </Stack>
            <Divider />
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Ubicación</TableCell>
                  <TableCell align="right">En mano</TableCell>
                  <TableCell>Última sincronización</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={`${r.locationId18}`}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {r.locationName}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: "monospace" }}
                      >
                        {r.quantityOnHand}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {r.lastSyncAt
                          ? new Date(r.lastSyncAt).toLocaleString()
                          : "-"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          size="small"
          variant="contained"
          onClick={onClose}
          color="primary"
        >
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default InventoryDetailsDialog;
